import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2, RotateCcw } from "lucide-react";

/**
 * Modal per generare uno shading map (luci/ombre) usato come layer dedicato nel
 * Surface Editor.
 *
 * - Sorgente immagine: SEMPRE quella effettivamente caricata in editor
 *   (`bgImageUrl`, prop). Risolve il bug in cui veniva usata la foto vecchia.
 * - Controlli avanzati (stile Photoshop): brightness, contrast, gamma + Curves
 *   editor.
 * - "Crea layer shading" → callback `onCreateLayer({shading_url, params})`.
 *   Ogni rigenerazione crea un layer NUOVO (file PNG univoco) — l'utente potrà
 *   eliminarlo o riordinarlo come gli altri layer.
 *
 * Props:
 *   - productId: string (richiesto per backend)
 *   - productName?: string
 *   - bgImageUrl?: string (URL sfondo CORRENTE — può essere il pendingBgUrl o la
 *     foto base del prodotto). Se assente, il modal lo recupera via API.
 *   - onClose()
 *   - onCreateLayer({shading_url, params, source_used})  (Surface Editor mode)
 *   - onApplied(shading_url)  (LEGACY: usato da CasaMadreProducts. In questa modalità
 *     il modal salva direttamente shading_url sul prodotto (`as_layer=false`).
 */
export default function ShadingMapModal({ productId, productName, bgImageUrl: bgImageUrlProp, onClose, onCreateLayer, onApplied }) {
  const isLayerMode = typeof onCreateLayer === "function";
  const [blur, setBlur] = useState(2.5);
  const [cutoff, setCutoff] = useState(2);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [gamma, setGamma] = useState(1.0);
  // Curves: array di control points [{x,y}] (0..255). Default: identità.
  const [curvePts, setCurvePts] = useState([{ x: 0, y: 0 }, { x: 128, y: 128 }, { x: 255, y: 255 }]);
  const [activeCurveIdx, setActiveCurveIdx] = useState(null);
  // Risoluzione bgImageUrl con fallback API (per uso fuori SurfaceEditor)
  const [bgImageUrl, setBgImageUrl] = useState(bgImageUrlProp || null);

  useEffect(() => { setBgImageUrl(bgImageUrlProp || null); }, [bgImageUrlProp]);
  useEffect(() => {
    if (bgImageUrlProp || !productId) return;
    (async () => {
      try {
        const r = await api.get(`/products/${productId}/surfaces`);
        const u = r.data?.background_image_url;
        if (u) setBgImageUrl(u);
      } catch { /* ignore */ }
    })();
  }, [productId, bgImageUrlProp]);

  const [generating, setGenerating] = useState(false);
  const [shadingUrl, setShadingUrl] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const debounceRef = useRef();

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  // Helper: resolve absolute URL for previews
  const absUrl = (u) => {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${apiBase}${u}`;
  };

  const runGenerate = useCallback(async () => {
    if (!bgImageUrl) {
      toast.error("Nessuna foto base caricata in editor");
      return;
    }
    setGenerating(true);
    try {
      const curvesJson = JSON.stringify(curvePts.map((p) => [Math.round(p.x), Math.round(p.y)]));
      const params = new URLSearchParams({
        blur_radius: String(blur),
        contrast_cutoff: String(cutoff),
        brightness: String(brightness),
        contrast: String(contrast),
        gamma: String(gamma),
        curves: curvesJson,
        source_url: bgImageUrl,
        as_layer: isLayerMode ? "true" : "false",
      });
      const r = await api.post(`/products/${productId}/generate-shading-map?${params.toString()}`);
      setShadingUrl(r.data.shading_url);
      setCacheBuster(Date.now());
    } catch (e) {
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  }, [bgImageUrl, blur, cutoff, brightness, contrast, gamma, curvePts, productId]);

  // Auto-generate al primo render e al cambio di un qualsiasi parametro (debounced)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { runGenerate(); }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [runGenerate]);

  const apply = () => {
    if (!shadingUrl) {
      toast.error("Nessuno shading generato");
      return;
    }
    const params = {
      blur_radius: blur, contrast_cutoff: cutoff,
      brightness, contrast, gamma,
      curves: curvePts.map((p) => [Math.round(p.x), Math.round(p.y)]),
      source_url: bgImageUrl,
    };
    if (isLayerMode) {
      onCreateLayer({ shading_url: shadingUrl, params, source_used: bgImageUrl });
      toast.success("Layer shading map creato");
    } else if (typeof onApplied === "function") {
      // Legacy: il file è già salvato come product.shading_url dal backend (as_layer=false)
      onApplied(shadingUrl);
      toast.success("Shading map applicata al prodotto");
    }
    onClose();
  };

  // ----- Curves editor (SVG 256×256) ----------------------------------------
  const CURVES_SIZE = 220;
  const curveToScreen = (p) => ({
    x: (p.x / 255) * CURVES_SIZE,
    y: CURVES_SIZE - (p.y / 255) * CURVES_SIZE,
  });
  const screenToCurve = (sx, sy) => ({
    x: Math.max(0, Math.min(255, (sx / CURVES_SIZE) * 255)),
    y: Math.max(0, Math.min(255, ((CURVES_SIZE - sy) / CURVES_SIZE) * 255)),
  });

  const buildCurvePath = () => {
    const sorted = [...curvePts].sort((a, b) => a.x - b.x);
    return sorted.map((p, i) => {
      const s = curveToScreen(p);
      return `${i === 0 ? "M" : "L"} ${s.x.toFixed(1)} ${s.y.toFixed(1)}`;
    }).join(" ");
  };

  const onCurveBgClick = (e) => {
    // Aggiunge un nuovo control point dove l'utente clicca (se non su un nodo esistente)
    if (activeCurveIdx !== null) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const p = screenToCurve(sx, sy);
    setCurvePts((pts) => {
      const next = [...pts, p].sort((a, b) => a.x - b.x);
      return next;
    });
  };

  const onCurveNodeMouseDown = (idx, e) => {
    e.stopPropagation();
    setActiveCurveIdx(idx);
  };

  useEffect(() => {
    if (activeCurveIdx === null) return;
    const move = (ev) => {
      const svg = document.getElementById("shading-curves-svg");
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;
      const p = screenToCurve(sx, sy);
      setCurvePts((pts) => pts.map((cp, i) => i === activeCurveIdx ? p : cp));
    };
    const up = () => setActiveCurveIdx(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [activeCurveIdx]);

  const removeCurvePt = (idx) => {
    if (curvePts.length <= 2) {
      toast.info("Almeno 2 punti necessari sulla curva");
      return;
    }
    setCurvePts((pts) => pts.filter((_, i) => i !== idx));
  };

  const resetCurves = () => setCurvePts([{ x: 0, y: 0 }, { x: 128, y: 128 }, { x: 255, y: 255 }]);
  const resetAll = () => {
    setBlur(2.5); setCutoff(2);
    setBrightness(0); setContrast(0); setGamma(1.0);
    resetCurves();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="shading-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-6xl w-full mx-4 max-h-[92vh] overflow-auto">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <div className="flex-1">
            <h3 className="font-bold text-base">Genera shading map (luci/ombre)</h3>
            <p className="text-xs text-muted-foreground">{productName || productId} · sorgente: {bgImageUrl ? "foto attiva in editor" : "nessuna"}</p>
          </div>
          <button onClick={resetAll} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="shading-reset-all">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-accent rounded-sm" data-testid="shading-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-12 gap-5">
          {/* Anteprime */}
          <div className="col-span-7 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Foto base attiva</div>
              <div className="aspect-square bg-neutral-100 rounded-sm overflow-hidden border border-border">
                {bgImageUrl ? (
                  <img src={absUrl(bgImageUrl)} alt="" className="w-full h-full object-contain" data-testid="shading-source-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Nessuna foto in editor</div>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2">
                Shading map (anteprima) {generating && <Loader2 className="w-3 h-3 animate-spin" />}
              </div>
              <div className="aspect-square bg-neutral-100 rounded-sm overflow-hidden border border-border relative">
                {shadingUrl ? (
                  <img src={`${absUrl(shadingUrl)}?t=${cacheBuster}`} alt="shading"
                       className="w-full h-full object-contain" data-testid="shading-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                    {generating ? "Generazione…" : "In attesa…"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Curves editor */}
          <div className="col-span-5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Curves (RGB)</span>
              <button onClick={resetCurves} className="text-[10px] text-muted-foreground hover:text-foreground underline" data-testid="shading-curves-reset">
                Reset curva
              </button>
            </div>
            <div className="bg-neutral-900 p-2 rounded-sm border border-border" style={{ width: CURVES_SIZE + 16 }}>
              <svg id="shading-curves-svg" width={CURVES_SIZE} height={CURVES_SIZE}
                onClick={onCurveBgClick}
                className="cursor-crosshair select-none" data-testid="shading-curves-svg">
                {/* Grid 4×4 */}
                {[1, 2, 3].map((i) => (
                  <g key={i}>
                    <line x1={(CURVES_SIZE / 4) * i} y1={0} x2={(CURVES_SIZE / 4) * i} y2={CURVES_SIZE} stroke="#1f2937" strokeWidth="1" />
                    <line x1={0} y1={(CURVES_SIZE / 4) * i} x2={CURVES_SIZE} y2={(CURVES_SIZE / 4) * i} stroke="#1f2937" strokeWidth="1" />
                  </g>
                ))}
                {/* Diagonale di riferimento */}
                <line x1={0} y1={CURVES_SIZE} x2={CURVES_SIZE} y2={0} stroke="#374151" strokeWidth="1" strokeDasharray="3,3" />
                {/* Curva */}
                <path d={buildCurvePath()} fill="none" stroke="#a855f7" strokeWidth="2" />
                {/* Nodi */}
                {[...curvePts].sort((a, b) => a.x - b.x).map((p, idx) => {
                  // Find original index
                  const origIdx = curvePts.indexOf(p);
                  const s = curveToScreen(p);
                  return (
                    <g key={origIdx}>
                      <circle cx={s.x} cy={s.y} r="6" fill="#a855f7" stroke="#fff" strokeWidth="1.5"
                        style={{ cursor: "grab" }}
                        onMouseDown={(e) => onCurveNodeMouseDown(origIdx, e)}
                        onDoubleClick={(e) => { e.stopPropagation(); removeCurvePt(origIdx); }}
                        data-testid={`shading-curve-pt-${idx}`} />
                    </g>
                  );
                })}
              </svg>
            </div>
            <div className="text-[10px] text-muted-foreground leading-tight">
              Click sfondo = aggiungi punto · Drag = sposta · Doppio click = elimina
            </div>
          </div>

          {/* Slider regolazioni */}
          <div className="col-span-12 grid grid-cols-2 gap-x-6 gap-y-3 pt-2 border-t border-border">
            <SliderRow label="Gaussian blur" hint="Bassi = dettagli · Alti = solo volumi" value={blur} setValue={setBlur} min={0} max={10} step={0.5} unit=" px" testid="shading-blur" />
            <SliderRow label="Autocontrast cutoff" hint="0 = no stretch · 20 = ombre nere/luci bianche" value={cutoff} setValue={setCutoff} min={0} max={20} step={1} unit="%" testid="shading-cutoff" />
            <SliderRow label="Luminosità" hint="-100 nero · 0 invariato · +100 doppio" value={brightness} setValue={setBrightness} min={-100} max={100} step={1} unit="" testid="shading-brightness" />
            <SliderRow label="Contrasto" hint="-100 piatto · 0 invariato · +100 estremo" value={contrast} setValue={setContrast} min={-100} max={100} step={1} unit="" testid="shading-contrast" />
            <SliderRow label="Gamma" hint="<1 schiarisce mezzitoni · >1 scurisce" value={gamma} setValue={setGamma} min={0.2} max={3.0} step={0.05} unit="" testid="shading-gamma" />
          </div>

          <div className="col-span-12 flex gap-2 pt-4 border-t border-border">
            <button onClick={onClose} className="flex-1 h-10 border border-border rounded-sm text-sm" data-testid="shading-cancel">Annulla</button>
            <button onClick={apply} disabled={!shadingUrl || generating}
              className="flex-1 h-10 bg-purple-600 text-white rounded-sm text-sm font-medium hover:bg-purple-700 disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
              data-testid="shading-apply">
              <Sparkles className="w-4 h-4" /> {isLayerMode ? "Crea layer shading" : "Salva shading map"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SliderRow({ label, hint, value, setValue, min, max, step, unit, testid }) {
  const display = typeof value === "number" ? (Number.isInteger(value) ? value.toString() : value.toFixed(2)) : value;
  return (
    <div>
      <label className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground mb-1">
        <span>{label}</span>
        <span className="font-mono text-foreground" data-testid={`${testid}-value`}>{display}{unit}</span>
      </label>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="w-full" data-testid={`${testid}-slider`} />
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}
